// Owns the socket and nothing else the UI cares about directly: hello/welcome with the stored token,
// automatic reconnect (with backoff, and a liveness watchdog for a peer that vanished without a close),
// dispatching every ServerMessage to one callback. The lobby and the online match view both just call
// `send()` and read messages from `onMessage` — neither touches a WebSocket, a timer, or localStorage;
// that is the whole point of this module existing.
import { PROTOCOL_VERSION, type ClientMessage, type ServerMessage } from '../../shared/protocol.js';
import { CURRENT_MAP_HASH } from '../../core/map/runtime.js';
import type { ClientSocket } from './socket.js';
import { readToken, writeToken } from './tokenStore.js';
import { playerKey } from './playerKey.js';

export type ConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'closed' | 'version-mismatch' | 'map-mismatch' | 'session-taken-elsewhere';

/** Bounds the queue a disconnected `send()` grows (see below) — generous over anything a lobby/HUD ever
    issues in one grace period, so it only ever protects against a genuine runaway, not real traffic. */
const MAX_QUEUE = 32;
/** Reconnect backoff: doubling from the base up to this cap, each wait drawn from its upper half so a
    server restart is not met by every client at the same instant. Reset by the next `welcome`. */
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 10_000;
/** Liveness: a socket whose peer vanished (a dropped Wi-Fi, a sleeping NAT) often never reports a close. In
    a match the server sends 20 snapshots a second; in the lobby it may send nothing for as long as nobody
    changes anything — so after PROBE_MS of silence this sends a `ping` of its own, and a connection that
    has answered nothing, not even that, for LIVENESS_MS is given up on and reopened. 5 s rather than less:
    a lossy mobile link can sit in TCP retransmission that long and still recover.
    The silence only counts while this page was actually running: a watchdog firing WATCHDOG_STALL_MS late
    means its own timers were held back (a hidden tab throttled to one run a minute, a main thread blocked
    by a long frame or GC, a phone waking up), so whatever the socket delivered meanwhile is still queued
    behind it. Such a run starts a fresh window with a probe instead of judging the socket. */
const PROBE_MS = 1000;
const LIVENESS_MS = 5000;
const WATCHDOG_STEP_MS = 250;
const WATCHDOG_STALL_MS = 1000;

export type ConnectionOptions = {
  /** Opens a fresh socket — called once up front and again on every reconnect attempt. */
  connect(): ClientSocket;
  onMessage(msg: ServerMessage): void;
  onStatus?(status: ConnectionStatus): void;
  /** Overrides localStorage — test/client/net/connection.test.ts uses a plain in-memory one. */
  tokenStore?: { get(): string | null; set(token: string): void };
  /** The first reconnect's wait before jitter (RECONNECT_BASE_MS); each failed attempt doubles it. */
  reconnectDelayMs?: number;
  /** 0..1, for the backoff's jitter — tests pass a fixed one. */
  random?(): number;
  /** Display name to offer in `hello` — already normalised (see protocol.ts's `SeatView.name` comment);
      the server re-normalises regardless. Read fresh on every `hello` (initial connect and every
      reconnect), not captured once, so a name entered after this connection was already opened still
      reaches the very next reconnect without the caller having to tear the connection down. */
  name?(): string | undefined;
};

export type Connection = {
  send(msg: ClientMessage): void;
  /** Explicit disconnect — the socket will not be reopened afterward. */
  close(): void;
  readonly status: ConnectionStatus;
  /** Last measured round trip in ms (client `ping.t` to the `pong` reply), or null before the first one
      lands. Unused today — stage 3's prediction/lag compensation is the intended reader; wired now so it
      doesn't need a protocol version bump later, per the same reasoning as `tick`/`dt`/`clock` already
      being on the wire (see protocol.ts's ClientMessage doc comment on `ping`). */
  readonly lastRttMs: number | null;
};

export function createConnection(opts: ConnectionOptions): Connection {
  const tokens = opts.tokenStore ?? { get: readToken, set: writeToken };
  const reconnectBaseMs = opts.reconnectDelayMs ?? RECONNECT_BASE_MS;
  const random = opts.random ?? Math.random;
  let failedAttempts = 0;
  let socket: ClientSocket | null = null;
  let status: ConnectionStatus = 'connecting';
  let closedByApp = false;
  let firstAttempt = true;
  // A real WebSocket throws (InvalidStateError) on send() while still CONNECTING — the caller has no
  // way to know that without asking `status` first, and callers besides `hello` itself (the lobby's
  // opening `listRooms`, a match's first command) have no reason to. Queue until `open`; a reconnect's
  // `hello` still goes out immediately from the onOpen handler below, ahead of anything queued earlier.
  // Bounded, and a `cmd` batch replaces any earlier queued one rather than appending: it already carries
  // the full unacked tail (commandChannel.ts's own resend buffer), so only the newest ever matters — a
  // long disconnect queueing every 4/s periodic resend would otherwise flush a stale burst on reconnect
  // and eat the command rate limit for several seconds on orders that don't matter anymore.
  let queue: ClientMessage[] = [];
  // Set the instant a PROTOCOL_MISMATCH error arrives, so the close that immediately follows it (the
  // server closes the connection right after sending it — see server.ts's handleHello) is recognized as
  // terminal: this client is stale and will send the exact same wrong version on every retry, so
  // reconnecting would just loop forever instead of surfacing "please reload" once.
  let versionMismatched = false;
  let mapMismatched = false;  // same terminal reasoning: this bundle was built against another map
  let sessionTaken = false;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let pingTimer: ReturnType<typeof setTimeout> | null = null;
  let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
  let pingCount = 0;
  let lastRttMs: number | null = null;
  const PING_INTERVAL_MS = 4000;
  // The first few pings go out fast: net/clockSync.ts's own warmup (a handful of samples averaged
  // together before its outlier gate engages, so one unlucky cold-start sample can't lock the whole
  // estimate) only converges as fast as samples arrive — at a flat 4s cadence that is ~12s into a match
  // before prediction's lead has any real numbers behind it. Settles into the normal cadence once warmed.
  const FAST_PING_INTERVAL_MS = 250;
  const FAST_PING_COUNT = 4;

  function setStatus(s: ConnectionStatus) { status = s; opts.onStatus?.(s); }

  function reconnectDelay() {
    const cap = Math.min(RECONNECT_MAX_MS, reconnectBaseMs * 2 ** failedAttempts);
    failedAttempts++;
    return cap / 2 + random() * cap / 2;
  }

  function stopTimers() {
    if (pingTimer) { clearTimeout(pingTimer); pingTimer = null; }
    if (watchdogTimer) { clearTimeout(watchdogTimer); watchdogTimer = null; }
  }

  function open() {
    setStatus(firstAttempt ? 'connecting' : 'reconnecting');
    firstAttempt = false;
    const sock = opts.connect();
    socket = sock;
    // Set once this socket is given up on, by its own close or by the watchdog — whichever comes first; a
    // close event the watchdog already acted on (it can come much later, or never) changes nothing then.
    let lost = false;
    let lastHeardAt = Date.now(), probedAt: number | null = null, scheduledAt = 0;
    function watch() {
      scheduledAt = Date.now();
      watchdogTimer = setTimeout(() => {
        const now = Date.now();
        const stalled = now - scheduledAt > WATCHDOG_STALL_MS;
        if (stalled) { lastHeardAt = now; probedAt = null; }
        else if (now - lastHeardAt >= LIVENESS_MS) { onLost(); sock.close(); return; }
        if (stalled || (now - lastHeardAt >= PROBE_MS && (probedAt === null || probedAt < lastHeardAt))) {
          probedAt = now;
          sock.send(JSON.stringify({ type: 'ping', t: now } satisfies ClientMessage));
        }
        watch();
      }, WATCHDOG_STEP_MS);
    }
    function onLost() {
      if (lost) return;
      lost = true;
      if (socket === sock) socket = null;
      stopTimers();
      if (closedByApp) { setStatus('closed'); return; }
      if (versionMismatched) { setStatus('version-mismatch'); return; }
      if (mapMismatched) { setStatus('map-mismatch'); return; }
      if (sessionTaken) { setStatus('session-taken-elsewhere'); return; }
      setStatus('reconnecting');
      reconnectTimer = setTimeout(open, reconnectDelay());
    }
    function schedulePing() {
      const delay = pingCount < FAST_PING_COUNT ? FAST_PING_INTERVAL_MS : PING_INTERVAL_MS;
      pingTimer = setTimeout(() => {
        sock.send(JSON.stringify({ type: 'ping', t: Date.now() } satisfies ClientMessage));
        pingCount++;
        schedulePing();
      }, delay);
    }
    sock.onOpen(() => {
      const token = tokens.get();
      sock.send(JSON.stringify({ type: 'hello', token: token ?? undefined, version: PROTOCOL_VERSION, mapHash: CURRENT_MAP_HASH, name: opts.name?.(), playerKey: playerKey() } satisfies ClientMessage));
    });
    sock.onMessage(data => {
      if (lost) return;
      lastHeardAt = Date.now();
      let msg: ServerMessage;
      try { msg = JSON.parse(data); } catch { return; }
      if (msg.type === 'welcome') {
        tokens.set(msg.token);
        failedAttempts = 0;
        setStatus('open');
        const pending = queue; queue = [];
        for (const m of pending) sock.send(JSON.stringify(m));
        stopTimers();
        pingCount = 0;
        schedulePing();
        watch();
      }
      if (msg.type === 'pong') lastRttMs = Date.now() - msg.t;
      // Answered here, straight off the socket, ahead of commandChannel.ts's batching and this module's
      // own disconnect queue: server/latency.ts's measurement is only as good as this reply's promptness,
      // and going through either would add this client's own send cadence to what is supposed to be a
      // clean round trip. See protocol.ts's `latencyPing` comment for why this is a second, separate
      // measurement from `ping`/`pong` above, not a duplicate of it.
      if (msg.type === 'latencyPing') sock.send(JSON.stringify({ type: 'latencyPong', seq: msg.seq } satisfies ClientMessage));
      if (msg.type === 'error' && msg.code === 'PROTOCOL_MISMATCH') versionMismatched = true;
      if (msg.type === 'error' && msg.code === 'MAP_MISMATCH') mapMismatched = true;
      // Terminal, same reasoning as PROTOCOL_MISMATCH: this connection's token was just taken over by
      // another tab, so reconnecting with the same (now-displaced-again) token would only restart the
      // takeover loop tokenStore.ts's own header describes — see server.ts's handleHello.
      if (msg.type === 'error' && msg.code === 'SESSION_TAKEN_ELSEWHERE') sessionTaken = true;
      opts.onMessage(msg);
    });
    sock.onClose(onLost);
  }
  open();

  return {
    send(msg) {
      if (status === 'open' && socket) { socket.send(JSON.stringify(msg)); return; }
      if (msg.type === 'cmd') {
        const i = queue.findIndex(q => q.type === 'cmd');
        if (i !== -1) queue.splice(i, 1);
      }
      queue.push(msg);
      if (queue.length > MAX_QUEUE) queue.shift();
    },
    close() {
      closedByApp = true;
      queue = [];
      if (reconnectTimer) clearTimeout(reconnectTimer);
      stopTimers();
      socket?.close();
      setStatus('closed');
    },
    get status() { return status; },
    get lastRttMs() { return lastRttMs; },
  };
}
